import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../config";
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  doc,
  getDoc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import NavBar from "./NavBar";

function AdminReferrals() {
  const [referrals, setReferrals] = useState([]);
  const [lastVisibleReferral, setLastVisibleReferral] = useState(null);
  const [loadingReferrals, setLoadingReferrals] = useState(false);
  const [error, setError] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();

  // Check if user is authenticated
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setIsAdmin(true);
        fetchReferrals();
      } else {
        navigate("/auth/login");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Fetch referrals with pagination
  const fetchReferrals = async (next = false) => {
    setLoadingReferrals(true);
    try {
      const referralsRef = collection(db, "referrals");
      let q = query(referralsRef, orderBy("timestamp", "desc"), limit(10));

      if (next && lastVisibleReferral) {
        q = query(
          referralsRef,
          orderBy("timestamp", "desc"),
          startAfter(lastVisibleReferral),
          limit(10)
        );
      }

      const snapshot = await getDocs(q);
      console.log("Referral snapshot:", snapshot);
      const data = await Promise.all(
        snapshot.docs.map(async (docdata) => {
          const referral = { id: docdata.id, ...docdata.data() };
          // Fetch referrer's full name
          let referrerName = "Unknown";
          if (referral.referrerId) {
            const referrerDoc = await getDoc(doc(db, "users", referral.referrerId));
            referrerName = referrerDoc.exists()
              ? referrerDoc.data().fullname || "Unknown"
              : "Unknown";
          }
          return { ...referral, referrerName };
        })
      );

      setReferrals((prev) => (next ? [...prev, ...data] : data));
      setLastVisibleReferral(snapshot.docs[snapshot.docs.length - 1]);
    } catch (error) {
      console.error("Error fetching referrals:", error);
      setError("Failed to load referrals: " + error.message);
    }
    setLoadingReferrals(false);
  };

  // Helper function to format timestamp
  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString();
    }
    if (timestamp instanceof Date) {
      return timestamp.toLocaleString();
    }
    if (typeof timestamp === "string") {
      const parsedDate = new Date(timestamp);
      return isNaN(parsedDate) ? "N/A" : parsedDate.toLocaleString();
    }
    return "N/A";
  };

  return (
    <div className="container-fluid px-3 py-4">
      {/* Top AdminLTE Navbar */}
      <nav className="main-header navbar navbar-expand navbar-white navbar-light mb-3">
        <ul className="navbar-nav">
          <li className="nav-item">
            <a className="nav-link" data-widget="pushmenu" href="#" role="button">
              <i className="fas fa-bars" />
            </a>
          </li>
        </ul>
      </nav>

      {/* Custom NavBar */}
      <NavBar />

      <div className="row justify-content-center">
        <div className="col-12 col-lg-10">
          <h3 className="text-center mb-4">Admin Referral Dashboard</h3>
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {/* Referrals Table */}
          <h4 className="text-left mb-3">All Referrals</h4>
          {loadingReferrals && (
            <div className="text-center mb-3">
              <i className="fas fa-spinner fa-spin fa-2x"></i> Loading Referrals...
            </div>
          )}
          <div className="table-responsive mb-5">
            <table className="table table-bordered table-hover">
              <thead className="table-dark">
                <tr>
                  <th>Referrer Name</th>
                  <th>Client Name</th>
                  <th>Client Email</th>
                  <th>Client Status</th>
                  <th>Date Registered</th>
                </tr>
              </thead>
              <tbody>
                {referrals.length === 0 && !loadingReferrals ? (
                  <tr>
                    <td colSpan="5" className="text-center">
                      No referrals found.
                    </td>
                  </tr>
                ) : (
                  referrals.map((referral) => (
                    <tr key={referral.id}>
                      <td>{referral.referrerName}</td>
                      <td>{referral.name || "N/A"}</td>
                      <td>{referral.email || "N/A"}</td>
                      <td>
                        <span className="badge bg-success">Active</span>
                      </td>
                      <td>{formatDate(referral.timestamp)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {lastVisibleReferral && (
            <div className="text-center mb-5">
              <button
                className="btn btn-outline-primary"
                onClick={() => fetchReferrals(true)}
                disabled={loadingReferrals}
              >
                Load More Referrals
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminReferrals;
